(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const cartUrl = `${root}cart.js`;
  const drawer = document.getElementById('CartDrawer');
  let lastFocus;
  const boundProductForms = new WeakSet();
  const pendingProductForms = new WeakSet();

  const money = (cents, currency = 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
  }).format(cents / 100);

  const updateProgress = (cart) => {
    document.querySelectorAll('[data-free-shipping-progress]').forEach((widget) => {
      const threshold = Number(widget.dataset.threshold) || 0;
      const subtotal = Number(cart.total_price) || 0;
      const capped = Math.min(subtotal, threshold);
      const remaining = Math.max(threshold - subtotal, 0);
      const currency = cart.currency || widget.dataset.currency || 'USD';
      widget.querySelector('[data-free-shipping-message]').textContent = remaining > 0
        ? `You're ${money(remaining, currency)} away from FREE shipping!`
        : `You've unlocked FREE shipping!`;
      widget.querySelector('[data-free-shipping-amount]').textContent = `${money(capped, currency)} / ${money(threshold, currency)} toward FREE shipping`;
      const track = widget.querySelector('[role="progressbar"]');
      const bar = widget.querySelector('[data-free-shipping-bar]');
      track.setAttribute('aria-valuenow', String(capped));
      bar.style.width = `${threshold > 0 ? Math.min((subtotal / threshold) * 100, 100) : 100}%`;
    });
  };

  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const renderDrawer = (cart) => {
    if (!drawer) return;
    const items = drawer.querySelector('[data-cart-drawer-items]');
    items.replaceChildren();
    if (!cart.items.length) items.append(make('p', 'ff-cart-drawer__empty', 'Your cart is currently empty.'));
    cart.items.forEach((item) => {
      const article = make('article', 'ff-cart-drawer__item');
      article.dataset.lineKey = item.key;
      if (item.image) {
        const link = make('a'); link.href = item.url;
        const image = make('img'); image.src = item.image; image.alt = item.product_title; image.loading = 'lazy'; image.width = 76; image.height = 76;
        link.append(image); article.append(link);
      } else article.append(make('span'));
      const details = make('div'); const title = make('a'); title.href = item.url;
      title.append(make('strong', '', item.product_title)); details.append(title);
      if (item.variant_title) details.append(make('small', '', item.variant_title));
      const visibleProperties = Object.entries(item.properties || {}).filter(([name, value]) => value && !name.startsWith('_'));
      if (visibleProperties.length) {
        const properties = make('dl', 'ff-cart-properties');
        visibleProperties.forEach(([name, value]) => {
          const row = make('div');
          row.append(make('dt', '', `${name}:`), make('dd', '', String(value)));
          properties.append(row);
        });
        details.append(properties);
      }
      const quantity = make('div', 'ff-cart-drawer__quantity');
      const minus = make('button', '', '−'); minus.type = 'button'; minus.dataset.cartChange = '-1'; minus.setAttribute('aria-label', `Decrease ${item.product_title} quantity`);
      const count = make('span', '', String(item.quantity));
      const plus = make('button', '', '+'); plus.type = 'button'; plus.dataset.cartChange = '1'; plus.setAttribute('aria-label', `Increase ${item.product_title} quantity`);
      quantity.append(minus, count, plus); details.append(quantity);
      const meta = make('div', 'ff-cart-drawer__item-meta');
      meta.append(make('strong', '', money(item.final_line_price, cart.currency)));
      const remove = make('button', 'ff-cart-remove'); remove.type = 'button'; remove.dataset.cartRemove = ''; remove.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
      remove.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5"></path></svg>';
      meta.append(remove); article.append(details, meta);
      items.append(article);
    });
    drawer.querySelector('[data-cart-drawer-subtotal]').textContent = money(cart.total_price, cart.currency);
  };

  const updateCartCount = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-drawer-open]').forEach((link) => {
      link.setAttribute('aria-label', `Cart with ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
    });
  };

  const updateCartPage = (cart) => {
    const page = document.querySelector('[data-cart-page]');
    if (!page) return;
    const form = page.querySelector('[data-cart-page-form]');
    const empty = page.querySelector('[data-cart-page-empty]');
    if (!cart.items.length) {
      if (form) form.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }
    if (form) form.hidden = false;
    if (empty) empty.hidden = true;
    const subtotal = document.querySelector('[data-cart-page-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price, cart.currency);
    document.querySelectorAll('[data-cart-page-line]').forEach((line) => {
      const item = cart.items.find((candidate) => candidate.key === line.dataset.cartPageLine)
        || cart.items.find((candidate) => String(candidate.variant_id) === line.dataset.cartPageVariant);
      if (!item) { line.remove(); return; }
      line.dataset.cartPageLine = item.key;
      const quantity = line.querySelector('[data-cart-page-quantity]');
      quantity.dataset.lineKey = item.key;
      quantity.value = item.quantity;
      line.querySelector('[data-cart-page-line-price]').textContent = money(item.final_line_price, cart.currency);
    });
  };

  const applyCart = (cart) => { updateProgress(cart); renderDrawer(cart); updateCartCount(cart); updateCartPage(cart); document.dispatchEvent(new CustomEvent('ff:cart-updated', { detail: { cart } })); return cart; };
  const getCart = () => fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json()).then(applyCart);
  const openDrawer = () => { if (!drawer) return; lastFocus = document.activeElement; drawer.setAttribute('aria-hidden', 'false'); document.body.classList.add('ff-cart-drawer-open'); drawer.querySelector('[data-cart-drawer-close]').focus(); };
  const closeDrawer = () => { if (!drawer) return; drawer.setAttribute('aria-hidden', 'true'); document.body.classList.remove('ff-cart-drawer-open'); lastFocus?.focus(); };

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-cart-drawer-open]');
    if (opener) { event.preventDefault(); await getCart(); openDrawer(); return; }
    if (event.target.closest('[data-cart-drawer-close]')) { closeDrawer(); return; }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.closest('[data-line-key], [data-cart-page-line]');
      const lineKey = line?.dataset.lineKey || line?.dataset.cartPageLine;
      if (!lineKey) return;
      remove.setAttribute('aria-disabled', 'true');
      const response = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: lineKey, quantity: 0 }) });
      if (response.ok) {
        const cart = await response.json();
        applyCart(cart);
      } else remove.removeAttribute('aria-disabled');
      return;
    }
    const change = event.target.closest('[data-cart-change]');
    if (change) {
      const line = change.closest('[data-line-key]');
      const current = Number(line.querySelector('.ff-cart-drawer__quantity span').textContent);
      change.disabled = true;
      const response = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.lineKey, quantity: Math.max(0, current + Number(change.dataset.cartChange)) }) });
      if (response.ok) applyCart(await response.json()); else change.disabled = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-cart-page-form]')) {
      if (event.submitter && event.submitter.name === 'checkout') return;
      event.preventDefault();
      const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (response.ok) applyCart(await response.json());
      return;
    }
  });

  // Handle the product form itself, before its submit event reaches document-level
  // Shopify listeners. Shopify's native AJAX tracking remains the only source of
  // commerce events; do not also publish product_added_to_cart or call fbq here.
  const onProductSubmit = async (event) => {
    const form = event.currentTarget;
    if (event.defaultPrevented || !form.checkValidity()) return;
    event.preventDefault();
    if (pendingProductForms.has(form)) return;
    pendingProductForms.add(form);
    const button = event.submitter || form.querySelector('[type="submit"]');
    const wasDisabled = button?.disabled;
    const previousBusy = form.getAttribute('aria-busy');
    form.setAttribute('aria-busy', 'true');
    let error = form.querySelector('[data-cart-add-error]');
    if (!error) {
      error = make('p', 'ff-cart-add-error');
      error.dataset.cartAddError = '';
      error.setAttribute('role', 'alert');
      form.append(error);
    }
    error.hidden = true;
    error.textContent = '';
    if (button) button.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new Error(typeof problem.description === 'string' ? problem.description : 'This item could not be added. Please check your selections and try again.');
      }
      // An accepted add must never be retried because refreshing the drawer failed.
      // Use the real cart page as the recovery path, preserving the successful add.
      try {
        await getCart();
        if (drawer) openDrawer();
        else window.location.assign(`${root}cart`);
      } catch {
        window.location.assign(`${root}cart`);
      }
    } catch (problem) {
      error.textContent = problem instanceof Error ? problem.message : 'Unable to reach the cart. Please check your connection.';
      error.hidden = false;
    } finally {
      pendingProductForms.delete(form);
      if (previousBusy === null) form.removeAttribute('aria-busy');
      else form.setAttribute('aria-busy', previousBusy);
      if (button) button.disabled = wasDisabled;
    }
  };

  const bindProductForms = (container) => {
    const forms = container instanceof HTMLFormElement ? [container] : [];
    forms.push(...container.querySelectorAll('form[action]'));
    forms.forEach((form) => {
      const action = new URL(form.action, window.location.href);
      if (action.origin !== window.location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname) || boundProductForms.has(form)) return;
      boundProductForms.add(form);
      form.addEventListener('submit', onProductSubmit);
    });
  };
  bindProductForms(document);
  // Quick-add content and Shopify Customize can insert or replace product forms.
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) bindProductForms(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-page-quantity]');
    if (!input) return;
    const response = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: input.dataset.lineKey, quantity: Math.max(0, Number(input.value) || 0) }) });
    if (response.ok) applyCart(await response.json());
  });

  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && drawer?.getAttribute('aria-hidden') === 'false') closeDrawer(); });
  ['cart:updated', 'cart:change', 'cart:refresh', 'product:added'].forEach((name) => document.addEventListener(name, getCart));
  getCart();
})();
